import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { playCue } from '../lib/foley';
import './home.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNewGame = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/new`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (!data.uuid) {
        throw new Error('Invalid response: missing game ID');
      }
      playCue('swoosh');
      navigate(`/g/${data.uuid}`);
    } catch (err) {
      playCue('error');
      console.error('Failed to create new game:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create a new game.';
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="home">
      <main role="main">
        {/* Hero Section */}
        <header className="home__hero">
          <h1 className="home__title">PANDA PLANNING POKER</h1>
          <p className="home__subtitle">shall we play a game?</p>

          {/* Decorative Cards */}
          <div className="home__cards-display" aria-hidden="true">
            <div className="home__card">5</div>
            <div className="home__card home__card--back"></div>
            <div className="home__card">8</div>
            <div className="home__card home__card--back"></div>
          </div>

          <div className="home__cta">
            <button
              className="home__button"
              onClick={handleNewGame}
              aria-label="Start a new game"
              disabled={isLoading}
              data-foley-press="press"
            >
              {isLoading ? 'Creating Game...' : 'Deal the Cards'}
            </button>
            {error && <p className="home__error">{error}</p>}
          </div>
        </header>

        {/* What is Planning Poker Section */}
        <section className="home__section" aria-labelledby="what-is">
          <h2 id="what-is" className="home__section-title">WHAT IS PLANNING POKER?</h2>
          <p className="home__section-intro">
            Planning Poker is a consensus-based estimation technique used by agile teams 
            to estimate the effort or complexity of user stories and tasks. Each team member 
            privately selects a card representing their estimate, then all cards are revealed 
            simultaneously.
          </p>
          <div className="home__benefits">
            <div className="home__benefit">
              <span className="home__benefit-icon" aria-hidden="true">🎯</span>
              <div className="home__benefit-content">
                <strong>Avoids Anchoring Bias</strong>
                <p>Hidden votes prevent early opinions from influencing others</p>
              </div>
            </div>
            <div className="home__benefit">
              <span className="home__benefit-icon" aria-hidden="true">💬</span>
              <div className="home__benefit-content">
                <strong>Encourages Discussion</strong>
                <p>Disagreements spark valuable conversations about scope</p>
              </div>
            </div>
            <div className="home__benefit">
              <span className="home__benefit-icon" aria-hidden="true">🤝</span>
              <div className="home__benefit-content">
                <strong>Builds Consensus</strong>
                <p>Teams align on expectations before work begins</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Panda Planning Poker Section */}
        <section className="home__section" aria-labelledby="why-panda">
          <h2 id="why-panda" className="home__section-title">WHY PANDA PLANNING POKER?</h2>
          <div className="home__features">
            <div className="home__feature">
              <span className="home__feature-icon" aria-hidden="true">🆓</span>
              <strong>Free Forever</strong>
              <p>Unlimited games, no signup required</p>
            </div>
            <div className="home__feature">
              <span className="home__feature-icon" aria-hidden="true">⚡</span>
              <strong>Real-Time</strong>
              <p>Instant sync across all players</p>
            </div>
            <div className="home__feature">
              <span className="home__feature-icon" aria-hidden="true">🎴</span>
              <strong>Multiple Card Sets</strong>
              <p>Fibonacci and T-shirt sizing</p>
            </div>
            <div className="home__feature">
              <span className="home__feature-icon" aria-hidden="true">🔗</span>
              <strong>Just Share a Link</strong>
              <p>No accounts, no downloads</p>
            </div>
            <div className="home__feature">
              <span className="home__feature-icon" aria-hidden="true">📱</span>
              <strong>Any Device</strong>
              <p>Desktop, tablet, or mobile</p>
            </div>
            <div className="home__feature">
              <span className="home__feature-icon" aria-hidden="true">🔒</span>
              <strong>Private Sessions</strong>
              <p>Only people with the link can join</p>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="home__section" aria-labelledby="how">
          <h2 id="how" className="home__section-title">HOW IT WORKS</h2>
          <div className="home__steps">
            <div className="home__step">
              <span className="home__step-number">1</span>
              <strong>Create a Game</strong>
              <p>Click "Deal the Cards" to get a unique session link</p>
            </div>
            <div className="home__step">
              <span className="home__step-number">2</span>
              <strong>Invite Your Team</strong>
              <p>Share the link — teammates enter their name to join</p>
            </div>
            <div className="home__step">
              <span className="home__step-number">3</span>
              <strong>Vote Privately</strong>
              <p>Everyone picks a card — see who voted, not what</p>
            </div>
            <div className="home__step">
              <span className="home__step-number">4</span>
              <strong>Reveal &amp; Discuss</strong>
              <p>All votes shown together, then reset for next round</p>
            </div>
          </div>
        </section>

        {/* Secondary CTA */}
        <div className="home__cta home__cta--secondary">
          <button
            className="home__button"
            onClick={handleNewGame}
            aria-label="Start a new game"
            disabled={isLoading}
            data-foley-press="press"
          >
            {isLoading ? 'Creating Game...' : 'Start Your Free Game'}
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="home__footer">
        <div className="home__footer-content">
          <div className="home__footer-brand">
            Another <a href="https://yayproject.com" target="_blank" rel="noopener noreferrer">Yay! Project</a>
          </div>
          <div className="home__footer-links">
            <a href="https://yayproject.com/contact" target="_blank" rel="noopener noreferrer">Contact</a>
            <span className="home__footer-divider">·</span>
            <a href="#">Privacy Policy</a>
            <span className="home__footer-divider">·</span>
            <a href="#">Terms of Service</a>
            <span className="home__footer-divider">·</span>
            <Link to="/made-with-ai">Made with AI</Link>
          </div>
          <div className="home__footer-copyright">
            © 2025 Adrian Lansdown.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
